import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { SnackMsg, SessionExpiredModal } from "../../components";
import { ADMIN_TYPE } from "../../utils/Constants";
import { PricingListStyled } from "./styled";
import { PRICING_COLUMNS, PRICING_TABS, FIELD_KEYS, RATE_COLUMN_KEYS } from "./constants";
import { fetchPricingTree, submitPricingChanges, validateRate, getNodeOriginalValue } from "./service";
import { PRICING_CONSTANTS } from "../../config/constants";
import PricingHeader from "./PricingHeader";
import PricingRow from "./PricingRow";

const PricingList = ({ showRatesColumns = true, reloadOnSelect = false }) => {
  const [activeTab, setActiveTab] = useState(PRICING_TABS.PRICING);
  const [nodes, setNodes] = useState([]);
  const [, setPendingCount] = useState(0);
  const [expandedNodeIds, setExpandedNodeIds] = useState({});
  const [dirtyValues, setDirtyValues] = useState({});
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snackMsg, setSnackMsg] = useState("");
  const [showSnack, setShowSnack] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);

  const snackTimerRef = useRef(null);

  const isSuperAdmin = useMemo(() => localStorage.getItem("role") === ADMIN_TYPE.SUPERADMIN, []);

  // Separate Standalone Dismiss Function
  const handleSnackClose = useCallback(() => {
    if (snackTimerRef.current) {
      clearTimeout(snackTimerRef.current);
      snackTimerRef.current = null;
    }
    setShowSnack(false);
  }, []);

  // Optimized helper to safely trigger snackbars without overlay collisions
  const triggerSnackMessage = useCallback((msg) => {
    // Clear any active auto-dismiss timers instantly before resetting
    if (snackTimerRef.current) {
      clearTimeout(snackTimerRef.current);
    }
    
    setSnackMsg(msg);
    setShowSnack(true);

    // Set up the fresh tracking sequence wrapper
    snackTimerRef.current = setTimeout(() => {
      setShowSnack(false);
    }, 4000);
  }, []);

  // Cleanup reference allocations when the view completely unmounts from DOM
  useEffect(() => {
    return () => {
      if (snackTimerRef.current) clearTimeout(snackTimerRef.current);
    };
  }, []);

  const childrenByParent = useMemo(() => {
    const map = {};
    nodes.forEach((node) => {
      const key = node.parentId ?? "root";
      if (!map[key]) map[key] = [];
      map[key].push(node);
    });
    return map;
  }, [nodes]);

  const loadPricingTree = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await fetchPricingTree();
      const fetchedNodes = response?.tree || [];
      setNodes(fetchedNodes);
      setPendingCount(response?.pendingCount || 0);
    } catch (error) {
      if (error?.message === "SESSION_EXPIRED") {
        setSessionExpired(true);
      } else {
        console.error("Failed to load pricing tree structural layout:", error);
        setNodes([]); 
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPricingTree();
  }, [loadPricingTree]);

  const visibleRows = useMemo(() => {
    const result = [];
    const addRows = (parentId = "root") => {
      const list = childrenByParent[parentId] || [];
      list.forEach((node) => {
        result.push(node);
        if (expandedNodeIds[node.id]) addRows(node.id);
      });
    };
    addRows("root");
    return result;
  }, [childrenByParent, expandedNodeIds]);

  const toggleNode = useCallback((nodeId) => {
    setExpandedNodeIds((prev) => {
      const isCollapsing = !!prev[nodeId];
      const nextState = { ...prev, [nodeId]: !prev[nodeId] };

      if (isCollapsing) {
        const gatherChildIds = (id) => {
          let ids = [];
          const children = childrenByParent[id] || [];
          children.forEach((child) => {
            ids.push(child.id);
            ids = ids.concat(gatherChildIds(child.id));
          });
          return ids;
        };

        const childIdsToClear = gatherChildIds(nodeId);
        setDirtyValues((prevDirty) => {
          const nextDirty = { ...prevDirty };
          childIdsToClear.forEach((id) => delete nextDirty[id]);
          return nextDirty;
        });
        setErrors((prevErrors) => {
          const nextErrors = { ...prevErrors };
          childIdsToClear.forEach((id) => delete nextErrors[id]);
          return nextErrors;
        });
      }
      return nextState;
    });
  }, [childrenByParent]);

  const handleNodeSelect = useCallback(async (nodeId) => {
    if (reloadOnSelect) await loadPricingTree();
  }, [reloadOnSelect, loadPricingTree]);

  const isFieldEditable = useCallback((node, field) => {
    const perms = node?.permissions || {};
    if (!perms.isEditable) return false;
    return field === FIELD_KEYS.mdrPercent ? !!perms.canEditMdrPercent : !!perms.canEditMdrFlat;
  }, []);

  const getFieldValue = useCallback((node, field, dirtyState) => {
    const dirtyNode = dirtyState[node.id] || {};
    if (dirtyNode[field] !== undefined) return dirtyNode[field];
    return getNodeOriginalValue(node, field);
  }, []);

  const handleFieldChange = useCallback((node, field, rawValue) => {
    handleNodeSelect(node.id);
    const trimmed = rawValue.trim();
    const originalValue = getNodeOriginalValue(node, field);
    
    const isBackToOriginal = trimmed === originalValue;
    const message = isBackToOriginal ? "" : validateRate(trimmed);

    setDirtyValues((prev) => {
      const currentNode = { ...(prev[node.id] || {}) };
      if (isBackToOriginal) delete currentNode[field];
      else currentNode[field] = trimmed;

      if (Object.keys(currentNode).length === 0) {
        const next = { ...prev };
        delete next[node.id];
        return next;
      }
      return { ...prev, [node.id]: currentNode };
    });

    setErrors((prev) => {
      const nodeErrors = { ...(prev[node.id] || {}) };
      if (message) nodeErrors[field] = message;
      else delete nodeErrors[field];

      if (Object.keys(nodeErrors).length === 0) {
        const next = { ...prev };
        delete next[node.id];
        return next;
      }
      return { ...prev, [node.id]: nodeErrors };
    });
  }, [handleNodeSelect]);

  const handleFieldBlur = useCallback((node, field, currentValue) => {
    const trimmedValue = currentValue.trim();
    const originalValue = getNodeOriginalValue(node, field);
    if (trimmedValue === "") {
      setDirtyValues((prev) => {
        const currentNode = { ...(prev[node.id] || {}) };
        delete currentNode[field];
        
        if (Object.keys(currentNode).length === 0) {
          const next = { ...prev };
          delete next[node.id];
          return next;
        }
        return { ...prev, [node.id]: currentNode };
      });

      setErrors((prev) => {
        const nodeErrors = { ...(prev[node.id] || {}) };
        delete nodeErrors[field];
        
        if (Object.keys(nodeErrors).length === 0) {
          const next = { ...prev };
          delete next[node.id];
          return next;
        }
        return { ...prev, [node.id]: nodeErrors };
      });
    } else {
      if (trimmedValue === originalValue) {
        setDirtyValues((prev) => {
          const currentNode = { ...(prev[node.id] || {}) };
          delete currentNode[field];
          if (Object.keys(currentNode).length === 0) {
            const next = { ...prev };
            delete next[node.id];
            return next;
          }
          return { ...prev, [node.id]: currentNode };
        });

        setErrors((prev) => {
          const nodeErrors = { ...(prev[node.id] || {}) };
          delete nodeErrors[field];
          if (Object.keys(nodeErrors).length === 0) {
            const next = { ...prev };
            delete next[node.id];
            return next;
          }
          return { ...prev, [node.id]: nodeErrors };
        });
      }
    }
  }, []);

  const isPristine = useMemo(() => Object.keys(dirtyValues).length === 0, [dirtyValues]);
  const hasValidationErrors = useMemo(() => Object.keys(errors).length > 0, [errors]);
  const submitDisabled = isLoading || isSubmitting || isPristine || hasValidationErrors || nodes.length === 0;

  const visibleColumns = useMemo(() => {
    if (showRatesColumns) return PRICING_COLUMNS;
    return PRICING_COLUMNS.filter((col) => !RATE_COLUMN_KEYS.includes(col.key));
  }, [showRatesColumns]);

  const gridTemplateColumns = useMemo(() => {
    return showRatesColumns ? "1.3fr 1fr 1fr 1fr 1fr 1fr 1fr" : "1.3fr 1fr 1fr 1fr 1fr";
  }, [showRatesColumns]);

  const handleSubmit = async () => {
    if (submitDisabled) return;
    setIsSubmitting(true);
    try {
      const response = await submitPricingChanges(dirtyValues);
      if (response?.status?.code === "Y" || !response?.status) {
        triggerSnackMessage(response?.status?.message || PRICING_CONSTANTS.SUBMIT_SUCCESS);
        setDirtyValues({});
        setErrors({});
        await loadPricingTree();
      } else {
        triggerSnackMessage(response?.status?.message || PRICING_CONSTANTS.SUBMIT_ERROR);
      }
    } catch (error) {
      if (error?.message === "SESSION_EXPIRED") {
        setSessionExpired(true);
      } else {
        triggerSnackMessage(PRICING_CONSTANTS.SUBMIT_ERROR);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isSuperAdmin) {
    return (
      <PricingListStyled>
        <div className="empty">{PRICING_CONSTANTS.ACCESS_DENIED}</div>
      </PricingListStyled>
    );
  }

  return (
    <PricingListStyled>
      {showSnack && (
        <SnackMsg 
          msg={snackMsg} 
          show={showSnack} 
          onClose={handleSnackClose} 
        />
      )}

      <PricingHeader
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        handleSubmit={handleSubmit}
        submitDisabled={submitDisabled}
        isSubmitting={isSubmitting}
      />

      {activeTab === PRICING_TABS.PENDING ? (
        <div className="tab-placeholder">{PRICING_CONSTANTS.PENDING_COMING_SOON}</div>
      ) : (
        <div className="grid" data-testid="pricing-table-grid">
          <div className="header-row" style={{ gridTemplateColumns }}>
            {visibleColumns.map((col) => (
              <div key={col.key} className="head-cell" style={{ paddingLeft: "24px" }}>
                {col.label}
              </div>
            ))}
          </div>

          <div className="grid-body">
            {isLoading ? (
              <div className="empty">{PRICING_CONSTANTS.LOADING}</div>
            ) : visibleRows.length === 0 ? (
              <div className="empty center-empty-msg" data-testid="no-data-overlay">
                No data found
              </div>
            ) : (
              visibleRows.map((node) => (
                <PricingRow
                  key={node.id}
                  node={node}
                  showRatesColumns={showRatesColumns}
                  gridTemplateColumns={gridTemplateColumns}
                  hasChildren={!!(childrenByParent[node.id] || []).length}
                  isExpanded={!!expandedNodeIds[node.id]}
                  percentValue={getFieldValue(node, FIELD_KEYS.mdrPercent, dirtyValues)}
                  flatValue={getFieldValue(node, FIELD_KEYS.mdrFlat, dirtyValues)}
                  percentEditable={isFieldEditable(node, FIELD_KEYS.mdrPercent)}
                  flatEditable={isFieldEditable(node, FIELD_KEYS.mdrFlat)}
                  nodeErrors={errors[node.id]}
                  onSelect={handleNodeSelect}
                  onToggle={toggleNode}
                  onFieldChange={handleFieldChange}
                  onBlur={(node, field, value) => handleFieldBlur(node, field, value)}
                />
              ))
            )}
          </div>
        </div>
      )}

      {sessionExpired ? (
        <SessionExpiredModal
          show={sessionExpired}
          onHide={() => setSessionExpired(false)}
        />
      ) : null}
    </PricingListStyled>
  );
};

export default PricingList;